import { anchorLabel, formatTimeSl, type CheckinStatus, type Occurrence } from '@planer/shared';
import { CalendarDays, Car, Check, Flag, Flame, Link2, Minus, MoveRight, X } from 'lucide-react';
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
  type PanInfo,
} from 'motion/react';
import { useRef } from 'react';
import { haptic } from '../lib/actions';
import { occColor } from '../lib/colors';
import { durationLabel, timeLabel, travelLabel } from '../lib/format';

const THRESHOLD = 84;

const STATUS_TEXT: Record<CheckinStatus, string> = {
  done: 'opravljeno',
  missed: 'ni opravljeno',
  skipped: 'preskočeno',
};

interface CheckNodeProps {
  occ: Occurrence;
  status: CheckinStatus | null | undefined;
  onToggle: () => void;
}

/** The round node on the timeline: one tap marks done (or clears it again). */
export function CheckNode({ occ, status, onToggle }: CheckNodeProps) {
  const color = occColor(occ);
  if (!occ.requiresCheck) {
    return (
      <span className="grid h-11 w-11 place-items-center" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      </span>
    );
  }
  const fill =
    status === 'done'
      ? 'var(--done)'
      : status === 'missed'
        ? 'var(--missed)'
        : status === 'skipped'
          ? 'var(--skip)'
          : 'var(--surface)';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={
        status === 'done'
          ? `${occ.title}: opravljeno. Tapni za preklic.`
          : `Označi ${occ.title} kot opravljeno`
      }
      aria-pressed={status === 'done'}
      data-testid="check-node"
      className="grid h-11 w-11 place-items-center"
    >
      <motion.span
        className="grid h-[26px] w-[26px] place-items-center rounded-full"
        style={{
          background: fill,
          boxShadow: status ? 'none' : `inset 0 0 0 2px ${color}`,
        }}
        initial={false}
        animate={{ scale: status ? [1, 1.18, 1] : 1 }}
        transition={{ duration: 0.28 }}
      >
        {status === 'done' && <Check size={16} strokeWidth={3} color="var(--on-status)" />}
        {status === 'missed' && <X size={15} strokeWidth={3} color="var(--on-status)" />}
        {status === 'skipped' && <Minus size={15} strokeWidth={3} color="var(--on-status)" />}
      </motion.span>
    </button>
  );
}

interface TimelineItemProps {
  occ: Occurrence;
  status: CheckinStatus | null | undefined;
  /** Started in the past but nobody checked it yet. */
  overdue: boolean;
  last: boolean;
  /** Days in a row (habits), shown from 2 on. */
  streak?: number;
  onStatus: (status: CheckinStatus | null) => void;
  onOpen: () => void;
}

/** One entry of the day: time, timeline node, and a card. Swipe right = done, left = not done. */
export function TimelineItem({
  occ,
  status,
  overdue,
  last,
  streak,
  onStatus,
  onOpen,
}: TimelineItemProps) {
  const x = useMotionValue(0);
  const doneOpacity = useTransform(x, [12, THRESHOLD], [0, 1]);
  const missedOpacity = useTransform(x, [-THRESHOLD, -12], [1, 0]);
  const iconScale = useTransform(
    x,
    [-THRESHOLD, -40, 0, 40, THRESHOLD],
    [1.15, 0.8, 0.8, 0.8, 1.15],
  );
  const dragged = useRef(false);
  const armed = useRef<'done' | 'missed' | null>(null);
  const swipeable = occ.requiresCheck;
  const color = occColor(occ);

  // Haptic tick when the swipe crosses the threshold, so you feel when letting go will count.
  useMotionValueEvent(x, 'change', (v) => {
    const next = v > THRESHOLD ? 'done' : v < -THRESHOLD ? 'missed' : null;
    if (next !== armed.current) {
      if (next) haptic(6);
      armed.current = next;
    }
  });

  function onDragEnd(_: unknown, info: PanInfo) {
    if (info.offset.x > THRESHOLD) onStatus('done');
    else if (info.offset.x < -THRESHOLD) onStatus('missed');
    void animate(x, 0, { type: 'spring', stiffness: 600, damping: 42 });
  }

  const toggle = () => {
    onStatus(status === 'done' ? null : 'done');
  };
  const travel = travelLabel(occ);
  const anchor = anchorLabel(occ);
  const description = [
    timeLabel(occ),
    occ.title,
    anchor,
    travel,
    occ.moved ? 'premaknjeno' : null,
    occ.requiresCheck
      ? status
        ? STATUS_TEXT[status]
        : overdue
          ? 'čaka na potrditev'
          : null
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  const muted = status === 'done' || status === 'skipped';

  return (
    <motion.li
      layout="position"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="flex items-stretch gap-1"
    >
      <div className="w-11 shrink-0 pt-[13px] text-right text-[13px] font-medium text-muted tabular-nums">
        {formatTimeSl(occ.startTime)}
      </div>
      <div className="relative flex w-11 shrink-0 flex-col items-center">
        <div className="z-10 pt-[3px]">
          <CheckNode occ={occ} status={status} onToggle={toggle} />
        </div>
        {!last && <div className="-mt-1 w-[2px] flex-1 rounded-full bg-line" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1 pb-2.5">
        <div className="relative overflow-hidden rounded-[var(--radius-card)]">
          {swipeable && (
            <>
              <motion.div
                aria-hidden
                style={{ opacity: doneOpacity }}
                className="absolute inset-0 flex items-center gap-2 bg-done pl-4 font-semibold text-on-status"
              >
                <motion.span style={{ scale: iconScale }} className="flex items-center gap-2">
                  <Check size={20} strokeWidth={3} /> Opravljeno
                </motion.span>
              </motion.div>
              <motion.div
                aria-hidden
                style={{ opacity: missedOpacity }}
                className="absolute inset-0 flex items-center justify-end gap-2 bg-missed pr-4 font-semibold text-on-status"
              >
                <motion.span style={{ scale: iconScale }} className="flex items-center gap-2">
                  Ni opravljeno <X size={20} strokeWidth={3} />
                </motion.span>
              </motion.div>
            </>
          )}
          <motion.div
            role="button"
            tabIndex={0}
            aria-label={description}
            data-testid="occurrence"
            data-status={status ?? 'open'}
            drag={swipeable ? 'x' : false}
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.55}
            dragSnapToOrigin
            style={{ x, touchAction: 'pan-y' }}
            onPointerDown={() => {
              dragged.current = false;
            }}
            onDragStart={() => {
              dragged.current = true;
            }}
            onDragEnd={onDragEnd}
            onTap={() => {
              if (!dragged.current) onOpen();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }}
            className="card relative flex min-h-[3.75rem] cursor-pointer items-center gap-3 overflow-hidden py-3 pr-3 pl-4 select-none"
          >
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 w-1"
              style={{ background: color, opacity: muted ? 0.4 : 1 }}
            />
            <div className="min-w-0 flex-1">
              <p
                className={`truncate text-[16px] leading-tight font-semibold ${
                  muted ? 'text-muted line-through decoration-subtle' : 'text-text'
                }`}
              >
                {occ.title}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-muted">
                {occ.calendar ? (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <CalendarDays size={13} className="shrink-0" />
                    <span className="truncate">
                      {timeLabel(occ)}
                      {occ.location ? ` · ${occ.location}` : ` · ${occ.calendar.name}`}
                    </span>
                  </span>
                ) : (
                  <span className="tabular-nums">
                    {occ.durationMin > 0 ? durationLabel(occ.durationMin) : 'opomnik'}
                  </span>
                )}
                {anchor && (
                  <span className="inline-flex items-center gap-1" data-testid="anchor">
                    <Link2 size={13} /> {anchor}
                  </span>
                )}
                {streak !== undefined && streak >= 2 && (
                  <span
                    className="inline-flex items-center gap-1 font-semibold text-mid-ink tabular-nums"
                    data-testid="streak"
                  >
                    <Flame size={12} aria-hidden /> {streak}
                    <span className="sr-only"> dni zapored</span>
                  </span>
                )}
                {travel && (
                  <span
                    className="inline-flex items-center gap-1 tabular-nums"
                    data-testid="travel"
                  >
                    <Car size={13} /> {travel}
                  </span>
                )}
                {occ.moved && (
                  <span className="inline-flex items-center gap-1 text-mid-ink">
                    <MoveRight size={13} /> premaknjeno
                  </span>
                )}
                {occ.priority === 'must' && !muted && (
                  <span className="inline-flex items-center gap-1">
                    <Flag size={12} /> nujno
                  </span>
                )}
                {overdue && !status && occ.requiresCheck && (
                  <span className="rounded-full bg-mid-soft px-2 py-px text-[12px] font-medium text-mid-ink">
                    čaka
                  </span>
                )}
                {status === 'missed' && (
                  <span className="rounded-full bg-missed-soft px-2 py-px text-[12px] font-medium text-missed-ink">
                    ni opravljeno
                  </span>
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.li>
  );
}

/** Red-dot "now" marker between timeline entries. */
export function NowLine({ time }: { time: string }) {
  return (
    <li className="flex items-center gap-1" aria-label={`Zdaj, ${time}`}>
      <div className="w-11 shrink-0 text-right text-[12px] font-bold text-accent tabular-nums">
        {time}
      </div>
      <div className="flex w-11 shrink-0 justify-center">
        <span className="h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-accent-soft" />
      </div>
      <div className="h-[2px] flex-1 rounded-full bg-accent" />
    </li>
  );
}
